import React, { useContext, useMemo, useRef } from 'react';
import { Context } from '../../../App';

const toNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const fmt = (value) => {
  if (value == null) return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toFixed(2);
};

const LAYER_PATTERNS = ['layer-a', 'layer-b', 'layer-c', 'layer-d', 'layer-e'];

const LayeredSoilProfileSvg = ({ layers, foundationDepth, foundationWidth, hEffective, variant }) => {
  const { theme: appTheme } = useContext(Context);
  const theme = variant === 'pdf-light' ? 'light' : appTheme;

  const uniqueIdRef = useRef(`layered-${Math.random().toString(36).slice(2, 10)}`);
  const uniqueId = uniqueIdRef.current;
  const svgRootId = `${uniqueId}-root`;

  const frameX = 76;
  const frameY = 36;
  const frameWidth = 638;
  const profileHeight = 370;
  const viewTop = -28;
  const viewHeight = 446;
  const axisGap = 12;
  const leftAxisX = frameX - axisGap;
  const rightAxisX = frameX + frameWidth + axisGap;

  // Keep theme-dependent colors explicit like Terzaghi sketch pattern.
  const svgBackgroundColor = theme === 'light' ? 'transparent' : '#1f1f1f';
  const svgBorderColor = theme === 'light' ? '#d8d8d8' : '#555555';
  const frameFillColor = theme === 'light' ? '#ffffff' : '#2a2a2a';
  const frameStrokeColor = theme === 'light' ? '#9a9a9a' : '#848484';
  const hatchColor = theme === 'light' ? '#4b4b4b' : '#d7d7d7';
  const layerStrokeColor = theme === 'light' ? '#444444' : '#ececec';
  const textMainColor = theme === 'light' ? '#1f1f1f' : '#e3e3e3';
  const textSecondaryColor = theme === 'light' ? '#2f2f2f' : '#d9d9d9';
  const axisColor = theme === 'light' ? '#6a6a6a' : '#d0d0d0';
  const concreteOutlineColor = theme === 'light' ? '#666666' : '#9f9f9f';
  const concreteBaseColor = theme === 'light' ? '#dddddd' : '#4a4a4a';
  const textBoxFillColor = theme === 'light' ? 'rgba(255, 255, 255, 0.92)' : 'rgba(38, 38, 38, 0.92)';
  const textBoxStrokeColor = theme === 'light' ? '#3f3f3f' : '#a8a8a8';

  const palette =
    theme === 'dark'
      ? {
          svgBg: svgBackgroundColor,
          svgBorder: svgBorderColor,
          frameFill: frameFillColor,
          frameStroke: frameStrokeColor,
          groundStroke: '#bcbcbc',
          groundLabel: '#d1d1d1',
          hatchStroke: hatchColor,
          hatchFill: hatchColor,
          layerStroke: layerStrokeColor,
          layerBadgeFill: textBoxFillColor,
          layerBadgeStroke: textBoxStrokeColor,
          textMain: textMainColor,
          textSecondary: textSecondaryColor,
          axisStroke: axisColor,
          axisTick: axisColor,
          axisSymbol: axisColor,
          concreteOutline: concreteOutlineColor,
          concreteBase: concreteBaseColor,
          concreteAgg1: '#8c8c8c',
          concreteAgg2: '#7c7c7c',
          concreteAgg3: '#6e6e6e',
          dimLine: '#e1e1e1',
          heDim: '#8dd38a',
          effectiveLine: '#78d2ff',
          effectiveText: '#bde8ff',
          emptyText: '#d9d9d9',
        }
      : {
          svgBg: svgBackgroundColor,
          svgBorder: svgBorderColor,
          frameFill: frameFillColor,
          frameStroke: frameStrokeColor,
          groundStroke: '#4f4f4f',
          groundLabel: '#4f4f4f',
          hatchStroke: hatchColor,
          hatchFill: hatchColor,
          layerStroke: layerStrokeColor,
          layerBadgeFill: textBoxFillColor,
          layerBadgeStroke: textBoxStrokeColor,
          textMain: textMainColor,
          textSecondary: textSecondaryColor,
          axisStroke: axisColor,
          axisTick: axisColor,
          axisSymbol: axisColor,
          concreteOutline: concreteOutlineColor,
          concreteBase: concreteBaseColor,
          concreteAgg1: '#b6b6b6',
          concreteAgg2: '#a9a9a9',
          concreteAgg3: '#9f9f9f',
          dimLine: '#2f2f2f',
          heDim: '#2e7d32',
          effectiveLine: '#1976d2',
          effectiveText: '#1565c0',
          emptyText: '#787878',
        };

  const mapped = useMemo(() => {
    const safeLayers = Array.isArray(layers) ? layers : [];
    const parsed = safeLayers.map((layer, index) => {
      const z1Num = toNum(layer?.z1);
      const z2Num = toNum(layer?.z2);
      const validDepth = z1Num != null && z2Num != null && z2Num > z1Num;
      const hiNum = validDepth ? z2Num - z1Num : null;

      return {
        id: layer?.id ?? index + 1,
        z1Num,
        z2Num,
        hiNum,
        formation: layer?.formation || '',
        cNum: toNum(layer?.c),
        phiNum: toNum(layer?.phi),
        validDepth,
        patternKey: LAYER_PATTERNS[index % LAYER_PATTERNS.length],
      };
    });

    const validLayers = parsed.filter((layer) => layer.validDepth);
    const maxLayerDepth = validLayers.reduce((max, layer) => Math.max(max, layer.z2Num), 0);
    const foundationDepthNum = toNum(foundationDepth);
    const foundationWidthNum = toNum(foundationWidth);
    const hEffectiveNum = toNum(hEffective);
    const effectiveDepthNum =
      foundationDepthNum != null && hEffectiveNum != null && hEffectiveNum >= 0 ? foundationDepthNum + hEffectiveNum : null;
    const displayDepthBase = Math.max(maxLayerDepth, foundationDepthNum || 0, effectiveDepthNum || 0);
    const displayDepth = Math.max(displayDepthBase, 1);

    const boundaryDepths = Array.from(
      new Set(
        [
          0,
          ...validLayers.flatMap((layer) => [layer.z1Num, layer.z2Num]),
          foundationDepthNum != null ? Math.max(0, foundationDepthNum) : null,
          effectiveDepthNum != null ? Math.max(0, effectiveDepthNum) : null,
          displayDepth,
        ].filter((v) => v != null),
      ),
    ).sort((a, b) => a - b);

    const lastValidLayer = validLayers.length > 0 ? validLayers[validLayers.length - 1] : null;
    const intervals = [];
    for (let i = 0; i < boundaryDepths.length - 1; i += 1) {
      const top = boundaryDepths[i];
      const bottom = boundaryDepths[i + 1];
      if (!(bottom > top)) continue;
      let layer = validLayers.find((l) => top >= l.z1Num && bottom <= l.z2Num);
      let extended = false;
      if (!layer && lastValidLayer && top >= lastValidLayer.z2Num) {
        layer = lastValidLayer;
        extended = true;
      }
      intervals.push({
        top,
        bottom,
        thickness: bottom - top,
        layerId: layer ? layer.id : null,
        patternKey: layer ? layer.patternKey : null,
        extended,
      });
    }

    const minLayerBand = validLayers.length > 0 ? Math.min(14, profileHeight / validLayers.length) : 0;
    const rawHeights = intervals.map((interval) => (interval.thickness / displayDepth) * profileHeight);
    const minHeights = intervals.map((interval) => (interval.layerId != null ? minLayerBand : 0));
    let scaledHeights = rawHeights.map((h, index) => Math.max(h, minHeights[index]));
    const sumScaled = scaledHeights.reduce((acc, h) => acc + h, 0);

    if (sumScaled > profileHeight) {
      const excess = sumScaled - profileHeight;
      const adjustable = scaledHeights.reduce((acc, h, index) => acc + (h - minHeights[index]), 0);
      if (adjustable > 0) {
        scaledHeights = scaledHeights.map((h, index) => h - ((h - minHeights[index]) / adjustable) * excess);
      } else if (scaledHeights.length > 0) {
        const equalH = profileHeight / scaledHeights.length;
        scaledHeights = scaledHeights.map(() => equalH);
      }
    }

    let cursorY = frameY;
    const intervalsWithY = intervals.map((interval, index) => {
      const height = scaledHeights[index];
      const y = cursorY;
      cursorY += height;
      return { ...interval, y, height };
    });

    const mapDepthToY = (depth) => {
      if (!Number.isFinite(depth)) return frameY;
      const clamped = Math.max(0, Math.min(depth, displayDepth));
      const interval = intervalsWithY.find((intv) => clamped >= intv.top && clamped <= intv.bottom);
      if (!interval) return frameY + profileHeight;
      const ratio = (clamped - interval.top) / interval.thickness;
      return interval.y + ratio * interval.height;
    };

    const positionedLayers = validLayers.map((layer) => {
      const y1 = mapDepthToY(layer.z1Num);
      const y2 = mapDepthToY(layer.z2Num);
      const height = Math.max(1, y2 - y1);
      return { ...layer, y: y1, height, midY: y1 + height / 2 };
    });

    const renderBands = intervalsWithY.map((interval) => ({
      y: interval.y,
      height: interval.height,
      patternKey: interval.patternKey,
      extended: interval.extended,
      layerId: interval.layerId,
    }));

    const foundationDepthClamped = foundationDepthNum != null ? Math.max(0, Math.min(foundationDepthNum, displayDepth)) : null;
    const foundationBottomY = foundationDepthClamped != null ? mapDepthToY(foundationDepthClamped) : null;
    const effectiveDepthClamped = effectiveDepthNum != null ? Math.max(0, Math.min(effectiveDepthNum, displayDepth)) : null;
    const effectiveDepthY = effectiveDepthClamped != null ? mapDepthToY(effectiveDepthClamped) : null;

    return {
      parsed,
      positionedLayers,
      renderBands,
      validLayers,
      totalDepth: maxLayerDepth,
      displayDepth,
      boundaryDepths,
      foundationDepthNum,
      foundationWidthNum,
      foundationBottomY,
      hEffectiveNum,
      effectiveDepthNum,
      effectiveDepthY,
      mapDepthToY,
    };
  }, [layers, foundationDepth, foundationWidth, hEffective]);

  return (
    <div style={{ maxWidth: 800, margin: '10px auto 14px auto' }}>
      <svg
        id={svgRootId}
        viewBox={`0 ${viewTop} 800 ${viewHeight}`}
        width="100%"
        role="img"
        aria-label="Layered soil profile preview"
        style={{
          display: 'block',
          border: variant === 'pdf-light' ? 'none' : `1px solid ${palette.svgBorder}`,
          borderRadius: 8,
          background: palette.svgBg,
        }}
      >
        <defs>
          <pattern id={`${uniqueId}-layer-a`} width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="2" x2="8" y2="2" className="layeredSvgHatchLine" />
            <line x1="0" y1="6" x2="8" y2="6" className="layeredSvgHatchLine" />
          </pattern>
          <pattern id={`${uniqueId}-layer-b`} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="8" className="layeredSvgHatchLine" />
          </pattern>
          <pattern id={`${uniqueId}-layer-c`} width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="0.8" className="layeredSvgHatchDot" />
            <circle cx="6" cy="6" r="0.8" className="layeredSvgHatchDot" />
          </pattern>
          <pattern id={`${uniqueId}-layer-d`} width="10" height="10" patternUnits="userSpaceOnUse">
            <polyline points="1,8 4,5 8,6" className="layeredSvgHatchPoly" />
            <polyline points="2,2 5,1 7,3" className="layeredSvgHatchPoly" />
          </pattern>
          <pattern id={`${uniqueId}-layer-e`} width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="7" x2="8" y2="7" className="layeredSvgHatchLine" />
            <line x1="7" y1="0" x2="7" y2="8" className="layeredSvgHatchLine" />
          </pattern>
          <pattern id={`${uniqueId}-concreteClassic`} patternUnits="userSpaceOnUse" width="18" height="18">
            <rect width="18" height="18" fill={palette.concreteBase} />
            <circle cx="3" cy="4" r="1.2" fill={palette.concreteAgg1} />
            <circle cx="9" cy="3" r="0.9" fill={palette.concreteAgg2} />
            <circle cx="14" cy="6" r="1.1" fill={palette.concreteAgg1} />
            <circle cx="5" cy="11" r="1.0" fill={palette.concreteAgg2} />
            <circle cx="11" cy="10" r="1.3" fill={palette.concreteAgg1} />
            <circle cx="15" cy="13" r="0.8" fill={palette.concreteAgg3} />
            <circle cx="7" cy="16" r="1.1" fill={palette.concreteAgg2} />
            <circle cx="1.5" cy="15" r="0.7" fill={palette.concreteAgg3} />
            <polygon points="12,14 13.2,15 12.2,16.3 10.9,15.5" fill={palette.concreteAgg1} />
            <polygon points="2,8 3,9 2.3,10.2 0.9,9.4" fill={palette.concreteAgg2} />
          </pattern>
          <marker id={`${uniqueId}-arrow-up`} markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
            <path d="M6,1 L0,3 L6,5 Z" fill={palette.dimLine} />
          </marker>
          <marker id={`${uniqueId}-arrow-down`} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,1 L6,3 L0,5 Z" fill={palette.dimLine} />
          </marker>
          <marker id={`${uniqueId}-arrow-up-he`} markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
            <path d="M6,1 L0,3 L6,5 Z" fill={palette.heDim} />
          </marker>
          <marker id={`${uniqueId}-arrow-down-he`} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,1 L6,3 L0,5 Z" fill={palette.heDim} />
          </marker>
          <marker id={`${uniqueId}-arrow-open-right`} markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <path d="M0,0 L7,3.5 L0,7" fill={palette.dimLine} />
          </marker>
          <marker id={`${uniqueId}-arrow-open-left`} markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
            <path d="M7,0 L0,3.5 L7,7" fill={palette.dimLine} />
          </marker>
        </defs>
        <style>{`
          #${svgRootId} .layeredSvgFrame { fill: ${palette.frameFill}; stroke: ${palette.frameStroke}; stroke-width: 0.8; }
          #${svgRootId} .layeredSvgGroundLine { stroke: ${palette.groundStroke}; stroke-width: 0.8; }
          #${svgRootId} .layeredSvgGroundTick { stroke: ${palette.groundStroke}; stroke-width: 0.7; }
          #${svgRootId} .layeredSvgGroundLabel { font-size: 9px; font-weight: 700; fill: ${palette.groundLabel}; }
          #${svgRootId} .layeredSvgHatchLine { stroke: ${palette.hatchStroke}; stroke-width: 0.55; }
          #${svgRootId} .layeredSvgHatchDot { fill: ${palette.hatchFill}; }
          #${svgRootId} .layeredSvgHatchPoly { fill: none; stroke: ${palette.hatchStroke}; stroke-width: 0.55; }
          #${svgRootId} .layeredSvgLayer { stroke: ${palette.layerStroke}; stroke-width: 0.65; opacity: 0.88; }
          #${svgRootId} .layeredSvgLayerIdBadge { fill: ${palette.layerBadgeFill}; stroke: ${palette.layerBadgeStroke}; stroke-width: 0.6; }
          #${svgRootId} .layeredSvgLayerIdText { font-size: 10px; font-weight: 700; fill: ${palette.textMain}; text-anchor: middle; }
          #${svgRootId} .layeredSvgLayerFormation { font-size: 9px; font-weight: 600; fill: ${palette.textMain}; text-anchor: middle; }
          #${svgRootId} .layeredSvgLayerPropsBadge { fill: ${palette.layerBadgeFill}; stroke: ${palette.layerBadgeStroke}; stroke-width: 0.6; }
          #${svgRootId} .layeredSvgLayerPropsText { font-size: 9px; font-weight: 700; fill: ${palette.textMain}; text-anchor: middle; }
          #${svgRootId} .layeredSvgExtendedText { font-size: 9px; font-weight: 700; fill: ${palette.textMain}; text-anchor: end; }
          #${svgRootId} .layeredSvgAxis { stroke: ${palette.axisStroke}; stroke-width: 0.7; }
          #${svgRootId} .layeredSvgAxisTick { stroke: ${palette.axisTick}; stroke-width: 0.6; }
          #${svgRootId} .layeredSvgAxisStartLabel { font-size: 8px; font-weight: 700; fill: ${palette.textSecondary}; text-anchor: middle; }
          #${svgRootId} .layeredSvgAxisStartSymbol { fill: ${palette.axisSymbol}; }
          #${svgRootId} .layeredSvgLeftAxisLabel { font-size: 9px; font-weight: 600; fill: ${palette.textSecondary}; text-anchor: end; }
          #${svgRootId} .layeredSvgRightAxisLabel { font-size: 9px; font-weight: 700; fill: ${palette.textSecondary}; text-anchor: start; }
          #${svgRootId} .layeredSvgFoundationOutline { stroke: ${palette.concreteOutline}; stroke-width: 0.7; }
          #${svgRootId} .layeredSvgDimLine { stroke: ${palette.dimLine}; stroke-width: 1; }
          #${svgRootId} .layeredSvgDimText { font-size: 10px; font-weight: 700; fill: ${palette.dimLine}; text-anchor: middle; }
          #${svgRootId} .layeredSvgDfLine { stroke: ${palette.dimLine}; stroke-width: 0.9; }
          #${svgRootId} .layeredSvgDfText { font-size: 9px; font-weight: 700; fill: ${palette.dimLine}; text-anchor: start; }
          #${svgRootId} .layeredSvgHeLine { stroke: ${palette.heDim}; stroke-width: 0.9; }
          #${svgRootId} .layeredSvgHeText { font-size: 9px; font-weight: 700; fill: ${palette.heDim}; text-anchor: start; }
          #${svgRootId} .layeredSvgHeDepthLine { stroke: ${palette.effectiveLine}; stroke-width: 1; stroke-dasharray: 4 2; }
          #${svgRootId} .layeredSvgHeDepthText { font-size: 9px; font-weight: 700; fill: ${palette.effectiveText}; text-anchor: middle; }
          #${svgRootId} .layeredSvgEmpty { font-size: 12px; fill: ${palette.emptyText}; text-anchor: middle; }
        `}</style>

        <rect x={frameX} y={frameY} width={frameWidth} height={profileHeight} className="layeredSvgFrame" />

        <g>
          <line x1={frameX} y1={frameY} x2={frameX + frameWidth} y2={frameY} className="layeredSvgGroundLine" />
          <line x1={frameX + 120} y1={frameY} x2={frameX + 124} y2={frameY - 4} className="layeredSvgGroundTick" />
          <line x1={frameX + 320} y1={frameY} x2={frameX + 324} y2={frameY - 4} className="layeredSvgGroundTick" />
          <line x1={frameX + 520} y1={frameY} x2={frameX + 524} y2={frameY - 4} className="layeredSvgGroundTick" />
          <text x={frameX + 8} y={frameY - 5} className="layeredSvgGroundLabel">
            Ground Surface
          </text>
        </g>

        {mapped.positionedLayers.length === 0 && (
          <text x="400" y={frameY + profileHeight / 2} className="layeredSvgEmpty">
            Add valid layers to preview the soil profile.
          </text>
        )}

        {mapped.renderBands.map((band, idx) => (
          <g key={`band-${idx}`}>
            <rect
              x={frameX}
              y={band.y}
              width={frameWidth}
              height={band.height}
              fill={band.patternKey ? `url(#${uniqueId}-${band.patternKey})` : 'transparent'}
              className="layeredSvgLayer"
            />
            {band.extended && (
              <>
                <rect
                  x={frameX + 8}
                  y={band.y + band.height / 2 - 7}
                  width="122"
                  height="14"
                  rx="2"
                  className="layeredSvgLayerIdBadge"
                />
                <text x={frameX + 69} y={band.y + band.height / 2 + 3} className="layeredSvgLayerIdText">
                  {`Layer-${band.layerId} | Extended`}
                </text>
              </>
            )}
          </g>
        ))}

        {mapped.effectiveDepthY != null && mapped.hEffectiveNum != null && mapped.hEffectiveNum > 0 && (
          <g>
            <line
              x1={frameX}
              y1={mapped.effectiveDepthY}
              x2={frameX + frameWidth}
              y2={mapped.effectiveDepthY}
              className="layeredSvgHeDepthLine"
            />
            <text x={frameX + frameWidth / 2} y={mapped.effectiveDepthY - 4} className="layeredSvgHeDepthText">
              Effective depth
            </text>
          </g>
        )}

        {mapped.positionedLayers.map((layer) => {
          const badgeY = layer.midY - 7;
          const propsBadgeWidth = 136;
          const propsBadgeX = frameX + frameWidth - propsBadgeWidth - 8;
          return (
            <g key={`layer-${layer.id}`}>
              <rect x={frameX + 8} y={badgeY} width="58" height="14" rx="2" className="layeredSvgLayerIdBadge" />
              <text x={frameX + 37} y={layer.midY + 3} className="layeredSvgLayerIdText">
                {`Layer-${layer.id}`}
              </text>
              {layer.formation && (
                <text x={frameX + frameWidth / 2} y={layer.midY + 3} className="layeredSvgLayerFormation">
                  {layer.formation}
                </text>
              )}
              <rect x={propsBadgeX} y={badgeY} width={propsBadgeWidth} height="14" rx="2" className="layeredSvgLayerPropsBadge" />
              <text x={propsBadgeX + propsBadgeWidth / 2} y={layer.midY + 3} className="layeredSvgLayerPropsText">
                {`c=${fmt(layer.cNum)} kPa, \u03C6=${fmt(layer.phiNum)}\u00B0`}
              </text>
            </g>
          );
        })}

        <line x1={leftAxisX} y1={frameY} x2={leftAxisX} y2={frameY + profileHeight} className="layeredSvgAxis" />
        <text x={leftAxisX} y={frameY - 14} className="layeredSvgAxisStartLabel">
          z
          <tspan baselineShift="sub" fontSize="7">
            i
          </tspan>
        </text>
        <polygon
          points={`${leftAxisX},${frameY - 4} ${leftAxisX - 3},${frameY - 10} ${leftAxisX + 3},${frameY - 10}`}
          className="layeredSvgAxisStartSymbol"
        />
        {mapped.boundaryDepths.map((depthValue) => {
          const y = mapped.mapDepthToY(depthValue);
          return (
            <g key={`z-axis-${depthValue}`}>
              <line x1={leftAxisX - 4} y1={y} x2={leftAxisX} y2={y} className="layeredSvgAxisTick" />
              <text x={leftAxisX - 6} y={y + 3} className="layeredSvgLeftAxisLabel">
                {`${fmt(depthValue)} m`}
              </text>
            </g>
          );
        })}

        <line x1={rightAxisX} y1={frameY} x2={rightAxisX} y2={frameY + profileHeight} className="layeredSvgAxis" />
        <text x={rightAxisX} y={frameY - 14} className="layeredSvgAxisStartLabel">
          H
          <tspan baselineShift="sub" fontSize="7">
            i
          </tspan>
        </text>
        <polygon
          points={`${rightAxisX},${frameY - 4} ${rightAxisX - 3},${frameY - 10} ${rightAxisX + 3},${frameY - 10}`}
          className="layeredSvgAxisStartSymbol"
        />
        {mapped.boundaryDepths.map((depthValue) => {
          const y = mapped.mapDepthToY(depthValue);
          return (
            <line
              key={`h-axis-tick-${depthValue}`}
              x1={rightAxisX}
              y1={y}
              x2={rightAxisX + 4}
              y2={y}
              className="layeredSvgAxisTick"
            />
          );
        })}
        {mapped.positionedLayers.map((layer) => (
          <g key={`h-axis-${layer.id}`}>
            <text x={rightAxisX + 8} y={layer.midY + 3} className="layeredSvgRightAxisLabel">
              H
              <tspan baselineShift="sub" fontSize="7">
                {layer.id}
              </tspan>
              {`=${fmt(layer.hiNum)} m`}
            </text>
          </g>
        ))}

        {mapped.foundationBottomY != null && (
          <g>
            {(() => {
              const cx = frameX + frameWidth / 2;
              const footingW = 144;
              const footingH = 22;
              const stemW = 36;
              const stemH = 36;
              const yFootBaseTop = mapped.foundationBottomY - footingH;
              const yStemTop = yFootBaseTop - stemH;
              const xLeft = cx - footingW / 2;
              const xRight = cx + footingW / 2;
              const xDf = xLeft - 72;

              return (
                <>
                  <rect
                    x={cx - stemW / 2}
                    y={yStemTop}
                    width={stemW}
                    height={stemH}
                    fill={`url(#${uniqueId}-concreteClassic)`}
                    className="layeredSvgFoundationOutline"
                  />
                  <rect
                    x={xLeft}
                    y={yFootBaseTop}
                    width={footingW}
                    height={footingH}
                    fill={`url(#${uniqueId}-concreteClassic)`}
                    className="layeredSvgFoundationOutline"
                  />

                  {mapped.foundationWidthNum != null && mapped.foundationWidthNum > 0 && (
                    <>
                      <line
                        className="layeredSvgDimLine"
                        x1={xLeft + 8}
                        y1={yFootBaseTop + 12}
                        x2={xRight - 8}
                        y2={yFootBaseTop + 12}
                        markerStart={`url(#${uniqueId}-arrow-open-left)`}
                        markerEnd={`url(#${uniqueId}-arrow-open-right)`}
                      />
                      <text className="layeredSvgDimText" x={cx} y={yFootBaseTop + 10}>
                        {`B=${fmt(mapped.foundationWidthNum)} m`}
                      </text>
                    </>
                  )}

                  {mapped.foundationDepthNum != null && mapped.foundationDepthNum > 0 && (
                    <>
                      <line className="layeredSvgDimLine" x1={xDf - 9} y1={frameY} x2={xDf + 9} y2={frameY} />
                      <line
                        className="layeredSvgDimLine"
                        x1={xDf - 9}
                        y1={mapped.foundationBottomY}
                        x2={xDf + 9}
                        y2={mapped.foundationBottomY}
                      />
                      <line
                        x1={xDf}
                        y1={frameY}
                        x2={xDf}
                        y2={mapped.foundationBottomY}
                        className="layeredSvgDfLine"
                        markerStart={`url(#${uniqueId}-arrow-up)`}
                        markerEnd={`url(#${uniqueId}-arrow-down)`}
                      />
                      <text x={xDf + 6} y={frameY + (mapped.foundationBottomY - frameY) / 2 + 3} className="layeredSvgDfText">
                        D
                        <tspan baselineShift="sub" fontSize="7">
                          f
                        </tspan>
                        {`=${fmt(mapped.foundationDepthNum)} m`}
                      </text>
                    </>
                  )}

                  {mapped.hEffectiveNum != null &&
                    mapped.hEffectiveNum > 0 &&
                    mapped.effectiveDepthY != null &&
                    mapped.effectiveDepthY > mapped.foundationBottomY && (
                      <>
                        <line
                          className="layeredSvgHeLine"
                          x1={xDf - 9}
                          y1={mapped.foundationBottomY}
                          x2={xDf + 9}
                          y2={mapped.foundationBottomY}
                        />
                        <line
                          className="layeredSvgHeLine"
                          x1={xDf - 9}
                          y1={mapped.effectiveDepthY}
                          x2={xDf + 9}
                          y2={mapped.effectiveDepthY}
                        />
                        <line
                          x1={xDf}
                          y1={mapped.foundationBottomY}
                          x2={xDf}
                          y2={mapped.effectiveDepthY}
                          className="layeredSvgHeLine"
                          markerStart={`url(#${uniqueId}-arrow-up-he)`}
                          markerEnd={`url(#${uniqueId}-arrow-down-he)`}
                        />
                        <text
                          x={xDf + 6}
                          y={mapped.foundationBottomY + (mapped.effectiveDepthY - mapped.foundationBottomY) / 2 + 3}
                          className="layeredSvgHeText"
                        >
                          H
                          <tspan baselineShift="sub" fontSize="7">
                            e
                          </tspan>
                          {`=${fmt(mapped.hEffectiveNum)} m`}
                        </text>
                      </>
                    )}
                </>
              );
            })()}
          </g>
        )}
      </svg>
    </div>
  );
};

export default LayeredSoilProfileSvg;
